import type { GroupClient } from '#src/groups/http/group_client.ts';
import type {
  CreateGroupErrorCode,
  CreateGroupRequest,
  CreateGroupSuccessResponse,
} from '@packages/contracts/group';
import { CreateGroupErrorCodes } from '@packages/contracts/group';
import { useMutation } from '@tanstack/react-query';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later.';

const ERROR_MESSAGES: Record<CreateGroupErrorCode, string> = {
  [CreateGroupErrorCodes.INVALID_INPUT]:
    'Some participant names or exclusions are not valid. Please review them and try again.',
  [CreateGroupErrorCodes.MATCHING_FAILED]:
    'Failed to match all participants. Please try again with less strict matching rules.',
};

/** A create request the server rejected with a known reason, as opposed to an unexpected failure. */
export class CreateGroupError extends Error {
  constructor(readonly code: CreateGroupErrorCode) {
    super(`Failed to create group: ${code}`);
    this.name = 'CreateGroupError';
  }
}

export function useCreateGroup(groupClient: GroupClient) {
  const mutation = useMutation<CreateGroupSuccessResponse, Error, CreateGroupRequest>({
    mutationKey: ['create-group'],
    mutationFn: async request => {
      const response = await groupClient.createGroup(request);
      if (!response.success) {
        throw new CreateGroupError(response.code);
      }
      return response;
    },
    retry: false,
  });

  const { data, error, ...rest } = mutation;

  return {
    ...rest,
    result: data,
    httpError: error,
    displayErrorMessage: getCreateErrorMessage(error),
  };
}

export function getCreateErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (error instanceof CreateGroupError) return ERROR_MESSAGES[error.code];
  return GENERIC_ERROR_MESSAGE;
}
